import {
  HHBaseTable,
  HHCard,
  HHIconButton,
  HHIconButtonMenu,
  HHTypography,
} from '@hinge-health/react-component-library';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import { ClientType } from '../../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../../utils/bills-utils';
import { formatCurrency } from '../../../../utils/currency-helpers';
import { cleanTypeNameGraphql } from '../../../../utils/graphql-utils';
import {
  BillTypeData,
  BillTypeDataList,
  GridColumnHeaderNames,
  InsurersType,
} from '../../bill-grid-components/bill-management-types';
import { SelectedHoldTypeCell } from './selected-hold-types-cell';
import { SimpleClientCell } from './simple-client-cell';
import { SimpleInsurerCell } from './simple-insurer-cell';

export type SelectedGridColumnId =
  | 'holdReason'
  | 'claimId'
  | 'billId'
  | 'costShareStatus'
  | 'billAmount'
  | 'userId'
  | 'dateOfService'
  | 'client'
  | 'insurer';

type CellValue = string | number | JSX.Element;

interface ColumnConfig {
  header: { value: string; align: 'left'; enableSort?: boolean };
  cell: (ctx: ColumnCellContext) => CellValue;
}

interface ColumnCellContext {
  row: BillTypeData;
  clients: ClientType[];
  insurers: InsurersType[];
  extraData?: object;
}

const COLUMN_REGISTRY: Record<SelectedGridColumnId, ColumnConfig> = {
  holdReason: {
    header: {
      value: GridColumnHeaderNames.HOLD_REASON,
      align: 'left',
      enableSort: false,
    },
    cell: ({ row }) => (
      <SelectedHoldTypeCell
        holdReason={row.holdDetails?.holdType}
        claimId={row.claimId}
      />
    ),
  },
  claimId: {
    header: { value: 'Claim Identifier', align: 'left' },
    cell: ({ row }) => row.claimId,
  },
  billId: {
    header: { value: GridColumnHeaderNames.BILL_ID, align: 'left' },
    cell: ({ row }) => row.billId,
  },
  costShareStatus: {
    header: { value: 'Cost Share Status', align: 'left' },
    cell: ({ extraData }) => {
      const data = extraData as { costShareStatus?: string | null } | undefined;
      return data?.costShareStatus
        ? capitalizeFirstLetterAndSpaceSnakeString(data.costShareStatus)
        : '--';
    },
  },
  billAmount: {
    header: { value: GridColumnHeaderNames.BILL_AMOUNT, align: 'left' },
    cell: ({ row }) =>
      Number.isFinite(row.billAmount)
        ? formatCurrency(row.billAmount as number)
        : '--',
  },
  userId: {
    header: { value: 'User', align: 'left' },
    cell: ({ row }) => row.userId ?? '--',
  },
  dateOfService: {
    header: { value: 'Date', align: 'left' },
    cell: ({ row }) => row.dateOfService,
  },
  client: {
    header: { value: GridColumnHeaderNames.CLIENT, align: 'left' },
    cell: ({ row, clients }) => (
      <SimpleClientCell clients={clients} details={row} />
    ),
  },
  insurer: {
    header: { value: GridColumnHeaderNames.INSURER, align: 'left' },
    cell: ({ row, insurers }) => (
      <SimpleInsurerCell insurers={insurers} details={row} />
    ),
  },
};

const DEFAULT_COLUMNS: SelectedGridColumnId[] = [
  'holdReason',
  'billAmount',
  'userId',
  'dateOfService',
  'client',
  'insurer',
];

export interface SelectedBillsGridProps {
  selectedRows: BillTypeDataList;
  setPanelSelectedRows: (selectedRows: BillTypeDataList) => void;
  clients: ClientType[];
  insurers: InsurersType[];
  columns?: SelectedGridColumnId[];
  extraRowData?: Map<number, object>;
}

export const SelectedBillsGrid = ({
  selectedRows,
  setPanelSelectedRows,
  clients,
  insurers,
  columns = DEFAULT_COLUMNS,
  extraRowData,
}: SelectedBillsGridProps): JSX.Element => {
  const handleDeleteRow = (rowId: number): void => {
    const rowsWithoutDeletedRow = selectedRows.filter(
      row => row.billId !== rowId,
    );
    setPanelSelectedRows(rowsWithoutDeletedRow);
  };

  const resolvedColumns = columns.map(id => ({
    id,
    ...COLUMN_REGISTRY[id],
  }));

  return (
    <HHCard>
      {/* Adding copy icon to make it easier for RCM to qa release changes */}
      <HHIconButton
        hhVariant="medium"
        onClick={(): void => {
          navigator.clipboard.writeText(
            JSON.stringify(
              cleanTypeNameGraphql(
                selectedRows.map(row => ({
                  claimId: row.claimId,
                  clientId: row.client.id,
                  clientsInsurerId: row.clientsInsurerId,
                  source: row.source,
                  sourceId: row.sourceId,
                  insurer: row.insurer.id,
                  billAmount: row.billAmount,
                  holdType: row.holdDetails?.holdType,
                  dateOfService: row.dateOfService,
                  holdDetails: row.holdDetails,
                })),
              ),
              null,
              2,
            ),
          );
        }}
      >
        <CopyAllIcon />
        <HHTypography hhVariant="muted">Copy rows</HHTypography>
      </HHIconButton>
      <HHBaseTable
        headerRowBottomBorder="1px solid rgba(0, 0, 0, 0.12)"
        bodyRowBorder={{
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
        bodyTypographyVariant="body2"
        tableHeaders={[
          ...resolvedColumns.map(col => col.header),
          { value: '', align: 'left' as const },
        ]}
        bodyContent={selectedRows.map(row => {
          const extra = extraRowData?.get(row.billId);
          const cells: Record<string, { value: CellValue }> =
            Object.fromEntries(
              resolvedColumns.map(col => [
                col.id,
                {
                  value: col.cell({
                    row,
                    clients,
                    insurers,
                    extraData: extra,
                  }),
                },
              ]),
            );
          cells.actions = {
            value:
              selectedRows.length <= 1 ? (
                ''
              ) : (
                <HHIconButtonMenu
                  aria-label="Grid action row menu"
                  role="menuitem"
                  menuItems={[
                    {
                      label: 'Remove from list',
                      onClick: () => handleDeleteRow(row.billId),
                    },
                  ]}
                />
              ),
          };
          return cells;
        })}
      />
    </HHCard>
  );
};
